import React, { useState, useEffect } from "react"
import { navigate } from "gatsby"
import "../styles/aiAudit.scss"
import {
  startAuditSession,
  submitAuditAnswer,
  submitAudit,
  generateDynamicQuestions,
  generateCompanySummary,
} from "../services/aiAuditService"

const WORD_LIMIT          = 15
const FREETEXT_WORD_LIMIT = 50
const OPTION_KEYS         = ["A", "B", "C", "D", "E"]

const countWords = str => str.trim().split(/\s+/).filter(Boolean).length

const PHASES = [
  { key: "industry", label: "Industry",     num: 1 },
  { key: "size",     label: "Company Size", num: 2 },
  { key: "audit",    label: "AI Audit",     num: 3 },
]

const CheckThin = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const SendIcon = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const StepIndicator = ({ currentPhase, completedPhases }) => (
  <div className="audit-steps">
    {PHASES.map((p, i) => {
      const isDone   = completedPhases.includes(p.key)
      const isActive = p.key === currentPhase
      return (
        <React.Fragment key={p.key}>
          <div className={`audit-step${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`}>
            <div className="audit-step__dot">
              {isDone ? <CheckThin /> : p.num}
            </div>
            <span className="audit-step__label">{p.label}</span>
          </div>
          {i < PHASES.length - 1 && (
            <div className={`audit-step__line${isDone ? " is-done" : ""}`} />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const AIAuditQuestionnaire = ({ userInfo }) => {
  const [step, setStep]                       = useState("questionnaire")
  const [sessionId, setSessionId]             = useState(null)
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [selectedOption, setSelectedOption]   = useState(null)
  const [otherText, setOtherText]             = useState("")
  const [loading, setLoading]                 = useState(true)
  const [answers, setAnswers]                 = useState([])
  const [animKey, setAnimKey]                 = useState(0)
  const [completedPhases, setCompletedPhases] = useState([])
  const [submitError, setSubmitError]         = useState(null)

  // Dynamic phase state
  const [dynamicQuestions, setDynamicQuestions] = useState([])
  const [dynamicIndex, setDynamicIndex]         = useState(0)
  const [dynamicAnswers, setDynamicAnswers]     = useState([])
  const [companySummary, setCompanySummary]     = useState(null)

  useEffect(() => {
    startAuditSession().then(({ sessionId: sid, question }) => {
      setSessionId(sid)
      setCurrentQuestion(question)
      setLoading(false)
    })
  }, [])

  // ── Static phase advancement ──────────────────────────────────────────────
  const advance = async (answer) => {
    const newAnswers = [...answers, { questionId: currentQuestion.id, answer }]
    setAnswers(newAnswers)
    setLoading(true)

    const res = await submitAuditAnswer({ sessionId, questionId: currentQuestion.id, answer })
    setLoading(false)

    if (res.done) {
      // Static questions complete — generate dynamic questions
      setStep("analyzing")
      try {
        const dynRes = await generateDynamicQuestions({
          staticAnswers: newAnswers,
          userInfo,
        })
        setDynamicQuestions(dynRes.questions)
        setCompletedPhases(["industry", "size"])
        setDynamicIndex(0)
        setSelectedOption(null)
        setOtherText("")
        setAnimKey(k => k + 1)
        setStep("dynamic")
      } catch {
        setSubmitError("We couldn't generate your personalized questions. Please try again.")
        setStep("error")
      }
    } else {
      if (res.question.phase !== currentQuestion.phase) {
        setCompletedPhases(p => [...p, currentQuestion.phase])
      }
      setAnimKey(k => k + 1)
      setSelectedOption(null)
      setOtherText("")
      setCurrentQuestion(res.question)
    }
  }

  // ── Dynamic phase advancement ─────────────────────────────────────────────
  const advanceDynamic = async (answer) => {
    const q = dynamicQuestions[dynamicIndex]
    const newDynAnswer = { questionId: q.id, answer, isDynamic: true, questionText: q.question }
    const updatedDyn = [...dynamicAnswers, newDynAnswer]
    setDynamicAnswers(updatedDyn)

    if (dynamicIndex + 1 >= dynamicQuestions.length) {
      // All dynamic questions answered — generate summary
      setStep("summarizing")
      try {
        const sumRes = await generateCompanySummary({
          allAnswers: [...answers, ...updatedDyn],
          userInfo,
        })
        setCompanySummary(sumRes.summary)
        setStep("summary")
      } catch {
        setSubmitError("We couldn't generate your company profile. Please try again.")
        setStep("error")
      }
    } else {
      setDynamicIndex(i => i + 1)
      setAnimKey(k => k + 1)
      setSelectedOption(null)
      setOtherText("")
    }
  }

  // ── Summary confirm → submit audit ───────────────────────────────────────
  const handleConfirm = async () => {
    setStep("generating")
    try {
      await submitAudit({
        sessionId,
        answers: [...answers, ...dynamicAnswers],
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        company: userInfo.companyName,
        designation: userInfo.designation,
        email: userInfo.email,
        country: userInfo.country,
      })
      setStep("confirmation")
    } catch {
      setSubmitError("Something went wrong generating your report. Please try again.")
      setStep("error")
    }
  }

  // ── Restart — full reset, keep userInfo/sessionId ────────────────────────
  const handleRestart = async () => {
    setAnswers([])
    setDynamicQuestions([])
    setDynamicIndex(0)
    setDynamicAnswers([])
    setCompanySummary(null)
    setCompletedPhases([])
    setSelectedOption(null)
    setOtherText("")
    setSubmitError(null)
    setLoading(true)
    setAnimKey(k => k + 1)
    const session = await startAuditSession()
    setSessionId(session.sessionId)
    setCurrentQuestion(session.question)
    setLoading(false)
    setStep("questionnaire")
  }

  // ── Option handlers ───────────────────────────────────────────────────────
  const handleOptionClick = async (opt) => {
    if (loading) return
    setSelectedOption(opt)
    if (opt === "Other") return
    await new Promise(r => setTimeout(r, 180))
    if (step === "dynamic") {
      await advanceDynamic(opt)
    } else {
      await advance(opt)
    }
  }

  const handleOtherSubmit = async () => {
    const trimmed = otherText.trim()
    if (!trimmed) return
    if (step === "dynamic") {
      await advanceDynamic(trimmed)
    } else {
      await advance(trimmed)
    }
  }

  const handleFreetextSubmit = async () => {
    const trimmed = otherText.trim()
    if (!trimmed) return
    if (step === "dynamic") {
      await advanceDynamic(trimmed)
    } else {
      await advance(trimmed)
    }
  }

  const handleTextChange = (e, wordLimit) => {
    const val   = e.target.value
    const words = val.trim().split(/\s+/).filter(Boolean)
    if (words.length <= wordLimit || val.length < otherText.length) setOtherText(val)
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const activeDynQ      = step === "dynamic" ? dynamicQuestions[dynamicIndex] : null
  const isFreetextQ     = (step === "questionnaire" && currentQuestion?.type === "freetext") ||
                          (step === "dynamic" && activeDynQ?.type === "freetext")
  const activeWordLimit = isFreetextQ ? FREETEXT_WORD_LIMIT : WORD_LIMIT
  const isOther         = selectedOption === "Other"
  const otherWords      = countWords(otherText)
  const otherValid      = otherWords > 0 && otherWords <= activeWordLimit

  // Progress
  const totalQ    = dynamicQuestions.length > 0 ? 5 + dynamicQuestions.length : "5+"
  const currentNum = step === "dynamic"
    ? 5 + dynamicIndex + 1
    : (currentQuestion?.progress?.current ?? 0)
  const totalNum  = step === "dynamic" ? (5 + dynamicQuestions.length) : 5
  const pct       = totalNum > 0 ? Math.round((currentNum / totalNum) * 100) : 0

  // Active question data for rendering
  const activeQuestion = step === "dynamic" ? activeDynQ : currentQuestion
  const activePhase    = step === "dynamic" ? "audit" : (currentQuestion?.phase ?? "industry")
  const allOptions     = activeQuestion
    ? [...(activeQuestion.options || []), ...(activeQuestion.allowOther ? ["Other"] : [])]
    : []

  return (
    <div className="audit-flow">

      {/* ── Initial loading ── */}
      {loading && !currentQuestion && (
        <div className="audit-flow__loading">
          <div className="audit-spinner" />
          <p>Preparing your questions…</p>
        </div>
      )}

      {/* ── Static questionnaire ── */}
      {step === "questionnaire" && currentQuestion && (
        <>
          <div className="audit-flow__header">
            <StepIndicator currentPhase={activePhase} completedPhases={completedPhases} />
            <span className="audit-flow__counter">
              {currentNum}<span>/{totalQ}</span>
            </span>
          </div>
          <div className="audit-flow__progress-row">
            <div className="audit-flow__progress-track">
              <div className="audit-flow__progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="audit-flow__progress-label">{pct}% complete</span>
          </div>

          <div key={animKey} className="audit-flow__body">
            <p className="audit-flow__q-num">{String(currentNum).padStart(2, "0")}</p>
            <h2 className="audit-flow__q-text">{currentQuestion.question}</h2>

            {isFreetextQ ? (
              // Freetext question (e.g. q4 department names)
              <div className="audit-other">
                {currentQuestion.hint && (
                  <p className="audit-flow__hint">{currentQuestion.hint}</p>
                )}
                <textarea
                  className="audit-other__input"
                  placeholder="Type your answer here…"
                  value={otherText}
                  onChange={e => handleTextChange(e, FREETEXT_WORD_LIMIT)}
                  rows={3}
                  autoFocus
                />
                <div className="audit-other__footer">
                  <span className={`audit-other__count${otherWords > FREETEXT_WORD_LIMIT ? " over" : ""}`}>
                    {otherWords} / {FREETEXT_WORD_LIMIT} words
                  </span>
                  <button
                    className="audit-btn audit-btn--primary"
                    onClick={handleFreetextSubmit}
                    disabled={!otherValid || loading}
                  >
                    {loading ? <><span className="audit-btn-spinner" /> Loading…</> : "Continue →"}
                  </button>
                </div>
              </div>
            ) : (
              // MCQ tiles
              <>
                {!isOther && <p className="audit-flow__hint">Select an option to continue</p>}
                <div className={`audit-tiles${allOptions.length <= 4 ? " audit-tiles--2col" : ""}`}>
                  {allOptions.map((opt, i) => (
                    <button
                      key={opt}
                      className={`audit-tile${selectedOption === opt ? " is-selected" : ""}${loading && selectedOption === opt ? " is-loading" : ""}`}
                      onClick={() => handleOptionClick(opt)}
                      disabled={loading}
                    >
                      <span className="audit-tile__key">{OPTION_KEYS[i]}</span>
                      <span className="audit-tile__label">{opt}</span>
                      {loading && selectedOption === opt && <span className="audit-tile__spinner" />}
                    </button>
                  ))}
                </div>
                {isOther && (
                  <div className="audit-other">
                    <textarea
                      className="audit-other__input"
                      placeholder="Describe in your own words…"
                      value={otherText}
                      onChange={e => handleTextChange(e, WORD_LIMIT)}
                      rows={2}
                      autoFocus
                    />
                    <div className="audit-other__footer">
                      <span className={`audit-other__count${otherWords > WORD_LIMIT ? " over" : ""}`}>
                        {otherWords} / {WORD_LIMIT} words
                      </span>
                      <button
                        className="audit-btn audit-btn--primary"
                        onClick={handleOtherSubmit}
                        disabled={!otherValid || loading}
                      >
                        {loading ? <><span className="audit-btn-spinner" /> Loading…</> : "Continue →"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Analyzing screen ── */}
      {step === "analyzing" && (
        <div className="audit-flow__analyzing">
          <div className="audit-spinner" />
          <h2 className="audit-flow__analyzing-title">Analyzing your organization's context…</h2>
          <p className="audit-flow__analyzing-sub">
            Tailoring the next questions specifically to <strong>{userInfo.companyName}</strong>.
          </p>
          <small className="audit-flow__analyzing-note">This takes a few seconds.</small>
        </div>
      )}

      {/* ── Dynamic questionnaire ── */}
      {step === "dynamic" && activeDynQ && (
        <>
          <div className="audit-flow__header">
            <StepIndicator currentPhase="audit" completedPhases={completedPhases} />
            <span className="audit-flow__counter">
              {currentNum}<span>/{totalQ}</span>
            </span>
          </div>
          <div className="audit-flow__progress-row">
            <div className="audit-flow__progress-track">
              <div className="audit-flow__progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="audit-flow__progress-label">{pct}% complete</span>
          </div>

          <div key={animKey} className="audit-flow__body">
            <p className="audit-flow__q-num">{String(currentNum).padStart(2, "0")}</p>
            <h2 className="audit-flow__q-text">{activeDynQ.question}</h2>
            {activeDynQ.hint && (
              <p className="audit-flow__hint audit-flow__hint--muted">{activeDynQ.hint}</p>
            )}

            {activeDynQ.type === "freetext" ? (
              <div className="audit-other">
                <textarea
                  className="audit-other__input"
                  placeholder="Share your thoughts…"
                  value={otherText}
                  onChange={e => handleTextChange(e, FREETEXT_WORD_LIMIT)}
                  rows={3}
                  autoFocus
                />
                <div className="audit-other__footer">
                  <span className={`audit-other__count${otherWords > FREETEXT_WORD_LIMIT ? " over" : ""}`}>
                    {otherWords} / {FREETEXT_WORD_LIMIT} words
                  </span>
                  <button
                    className="audit-btn audit-btn--primary"
                    onClick={handleFreetextSubmit}
                    disabled={!otherValid}
                  >
                    {dynamicIndex + 1 >= dynamicQuestions.length ? "Finish →" : "Continue →"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!isOther && <p className="audit-flow__hint">Select an option to continue</p>}
                <div className={`audit-tiles${allOptions.length <= 4 ? " audit-tiles--2col" : ""}`}>
                  {allOptions.map((opt, i) => (
                    <button
                      key={opt}
                      className={`audit-tile${selectedOption === opt ? " is-selected" : ""}`}
                      onClick={() => handleOptionClick(opt)}
                    >
                      <span className="audit-tile__key">{OPTION_KEYS[i]}</span>
                      <span className="audit-tile__label">{opt}</span>
                    </button>
                  ))}
                </div>
                {isOther && (
                  <div className="audit-other">
                    <textarea
                      className="audit-other__input"
                      placeholder="Describe in your own words…"
                      value={otherText}
                      onChange={e => handleTextChange(e, WORD_LIMIT)}
                      rows={2}
                      autoFocus
                    />
                    <div className="audit-other__footer">
                      <span className={`audit-other__count${otherWords > WORD_LIMIT ? " over" : ""}`}>
                        {otherWords} / {WORD_LIMIT} words
                      </span>
                      <button
                        className="audit-btn audit-btn--primary"
                        onClick={handleOtherSubmit}
                        disabled={!otherValid}
                      >
                        {dynamicIndex + 1 >= dynamicQuestions.length ? "Finish →" : "Continue →"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Summarizing screen ── */}
      {step === "summarizing" && (
        <div className="audit-flow__analyzing">
          <div className="audit-spinner" />
          <h2 className="audit-flow__analyzing-title">Building your company profile…</h2>
          <p className="audit-flow__analyzing-sub">
            Reviewing everything you've shared to form a clear picture of <strong>{userInfo.companyName}</strong>.
          </p>
        </div>
      )}

      {/* ── Summary confirmation ── */}
      {step === "summary" && companySummary && (
        <div className="audit-flow__summary">
          <h2 className="audit-flow__heading">Here's our understanding of {userInfo.companyName}</h2>
          <p className="audit-summary__headline">{companySummary.headline}</p>
          <ul className="audit-summary__bullets">
            {companySummary.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          <p className="audit-summary__question">Does this accurately represent your organization?</p>
          <div className="audit-summary__actions">
            <button className="audit-btn audit-btn--primary" onClick={handleConfirm}>
              Yes, generate my report
            </button>
            <button className="audit-btn audit-btn--ghost" onClick={handleRestart}>
              No, restart survey
            </button>
          </div>
        </div>
      )}

      {/* ── Generating report ── */}
      {step === "generating" && (
        <div className="audit-flow__loading">
          <div className="audit-spinner" />
          <p>Analysing your responses and generating your report…</p>
          <p className="audit-flow__note">This may take a minute. Please don't close this page.</p>
        </div>
      )}

      {/* ── Confirmation ── */}
      {step === "confirmation" && (
        <div className="audit-flow__center">
          <div className="audit-flow__icon-wrap audit-flow__icon-wrap--navy">
            <SendIcon />
          </div>
          <h2 className="audit-flow__heading">Your Report Is On Its Way</h2>
          <p className="audit-flow__sub">
            We're generating your personalised AI Audit Report based on your responses.
            It will arrive at <strong>{userInfo.email}</strong> within the next few minutes.
          </p>
          <p className="audit-flow__note audit-flow__note--spaced">
            Can't find it? Check your spam or junk folder, or contact us at{" "}
            <a href="mailto:info@visiomate.com" className="audit-flow__link">info@visiomate.com</a>.
          </p>
          <button className="audit-btn audit-btn--ghost" onClick={() => navigate("/")}>
            Back to Home
          </button>
        </div>
      )}

      {/* ── Error ── */}
      {step === "error" && (
        <div className="audit-flow__center">
          <h2 className="audit-flow__heading">Something Went Wrong</h2>
          <p className="audit-flow__sub">{submitError}</p>
          <p className="audit-flow__note">
            Please contact us at{" "}
            <a href="mailto:info@visiomate.com" className="audit-flow__link">info@visiomate.com</a>{" "}
            and we'll sort it out.
          </p>
          <button className="audit-btn audit-btn--ghost" onClick={() => navigate("/")}>
            Back to Home
          </button>
        </div>
      )}

    </div>
  )
}

export default AIAuditQuestionnaire
